import {
  Button,
  type ComboboxItem,
  type ComboboxLikeRenderOptionInput,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { schemaResolver, useForm } from '@mantine/form';
import {
  Icon123,
  IconCalendarTime,
  IconCheckbox,
  IconFileInvoice,
  IconForms,
  IconUser,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import type { ReactNode } from 'react';
import { z } from 'zod/v4';
import type { CustomVariable } from '~/modules/Settings/Variables/CustomVariables/api';

type CustomVariableFormState = {
  name: string;
  varType: string;
  modelType: string;
  display: string | null;
  defaultValue: string | number | boolean | dayjs.Dayjs | null;
  api: boolean;
  webhook: boolean;
};

export type CustomVariableFormValues = Omit<CustomVariableFormState, 'defaultValue'> & {
  defaultValue: string | number | boolean | null;
};

type Props = {
  onSubmit: (values: CustomVariableFormValues) => void;
  initialValues?: CustomVariable;
};

const ICON_PROPS = { size: 16, stroke: 1.5 };

const VAR_TYPE_ICONS: Record<string, ReactNode> = {
  string: <IconForms {...ICON_PROPS} />,
  number: <Icon123 {...ICON_PROPS} />,
  boolean: <IconCheckbox {...ICON_PROPS} />,
  date: <IconCalendarTime {...ICON_PROPS} />,
};

const MODEL_TYPE_ICONS: Record<string, ReactNode> = {
  debtor: <IconUser {...ICON_PROPS} />,
  invoice: <IconFileInvoice {...ICON_PROPS} />,
};

const renderOptionWithIcon = ({ option }: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
  const { icon, label } = option as ComboboxItem & { icon?: ReactNode };
  return (
    <Group gap="xs" wrap="nowrap">
      {icon}
      <Text size="sm">{label}</Text>
    </Group>
  );
};

export default function CustomVariableForm({ onSubmit, initialValues }: Props) {
  const { t } = useTranslation();

  const requiredMessage = t(i18nKeys.ERROR.FIELD_REQUIRED);
  const validationSchema = z.object({
    name: z.string().min(1, { error: requiredMessage }),
    varType: z.string().min(1, { error: requiredMessage }),
    modelType: z.string().min(1, { error: requiredMessage }),
  });

  const form = useForm<CustomVariableFormState>({
    mode: 'controlled',
    initialValues: {
      name: initialValues?.name ?? '',
      varType: initialValues?.varType ?? 'string',
      modelType: initialValues?.modelType ?? 'debtor',
      display: initialValues?.display ?? null,
      defaultValue: initialValues?.defaultValue ?? null,
      api: initialValues?.api ?? false,
      webhook: initialValues?.webhook ?? false,
    },
    validate: schemaResolver(validationSchema, { sync: true }),
  });

  const isNewForm = initialValues?.id === undefined;
  const { varType, modelType, defaultValue } = form.getValues();

  const defaultValueLabel = t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.DEFAULT_VALUE);

  const varTypeData: Array<ComboboxItem & { icon: ReactNode }> = [
    { value: 'string', label: t(i18nKeys.COLUMN_TYPE.STRING), icon: VAR_TYPE_ICONS.string },
    { value: 'number', label: t(i18nKeys.COLUMN_TYPE.NUMBER), icon: VAR_TYPE_ICONS.number },
    { value: 'boolean', label: t(i18nKeys.COLUMN_TYPE.BOOLEAN), icon: VAR_TYPE_ICONS.boolean },
    { value: 'date', label: t(i18nKeys.COLUMN_TYPE.DATE), icon: VAR_TYPE_ICONS.date },
  ];

  const modelTypeData: Array<ComboboxItem & { icon: ReactNode }> = [
    { value: 'debtor', label: t(i18nKeys.COMMON.CLIENT), icon: MODEL_TYPE_ICONS.debtor },
    { value: 'invoice', label: t(i18nKeys.COMMON.INVOICE), icon: MODEL_TYPE_ICONS.invoice },
  ];

  const renderDefaultValueField = () => {
    switch (varType) {
      case 'boolean':
        return (
          <Select
            label={defaultValueLabel}
            placeholder={t(i18nKeys.SELECT)}
            data={[
              { label: t(i18nKeys.YES), value: 'true' },
              { label: t(i18nKeys.NO), value: 'false' },
            ]}
            value={typeof defaultValue === 'string' ? defaultValue : null}
            onChange={value => form.setFieldValue('defaultValue', value)}
          />
        );
      case 'date':
        return (
          <DateInput
            label={defaultValueLabel}
            placeholder={t(i18nKeys.FORM.SELECT_DATE)}
            valueFormat="DD/MM/YYYY"
            clearable
            value={dayjs.isDayjs(defaultValue) ? defaultValue.toDate() : null}
            onChange={value => form.setFieldValue('defaultValue', value ? dayjs(value) : null)}
          />
        );
      default: {
        const textValue =
          defaultValue == null || dayjs.isDayjs(defaultValue) ? '' : String(defaultValue);
        return (
          <TextInput
            label={defaultValueLabel}
            placeholder={
              varType === 'number'
                ? t(i18nKeys.FORM.CLIENT.PLACEHOLDER.CUSTOM_VARIABLE_NUMBER)
                : t(i18nKeys.FORM.CLIENT.PLACEHOLDER.CUSTOM_VARIABLE_TEXT)
            }
            type={varType === 'number' ? 'number' : 'text'}
            step="0.01"
            value={textValue}
            onChange={event => form.setFieldValue('defaultValue', event.currentTarget.value)}
          />
        );
      }
    }
  };

  return (
    <form
      onSubmit={form.onSubmit(({ defaultValue: rawDefaultValue, ...rest }) =>
        onSubmit({
          ...rest,
          defaultValue: dayjs.isDayjs(rawDefaultValue)
            ? rawDefaultValue.format('YYYY-MM-DD')
            : rawDefaultValue,
        }),
      )}
    >
      <Stack gap="md">
        <TextInput
          label={t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.NAME)}
          placeholder={t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.NAME_PLACEHOLDER)}
          data-autofocus
          {...form.getInputProps('name')}
        />
        <SimpleGrid cols={2}>
          <Select
            label={t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.VAR_TYPE)}
            disabled={!isNewForm}
            allowDeselect={false}
            data={varTypeData}
            renderOption={renderOptionWithIcon}
            leftSection={VAR_TYPE_ICONS[varType]}
            {...form.getInputProps('varType')}
          />
          <Select
            label={t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.MODEL_TYPE)}
            disabled={!isNewForm}
            allowDeselect={false}
            data={modelTypeData}
            renderOption={renderOptionWithIcon}
            leftSection={MODEL_TYPE_ICONS[modelType]}
            {...form.getInputProps('modelType')}
          />
        </SimpleGrid>
        {varType === 'number' && (
          <Select
            label={t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.DISPLAY)}
            placeholder={t(i18nKeys.DEFAULT)}
            clearable
            data={[
              { label: t(i18nKeys.DEBTOR.CURRENCY), value: 'money' },
              {
                label: `${t(i18nKeys.DEBTOR.CURRENCY)} (${t(i18nKeys.IN_CENTS)})`,
                value: 'money_in_cents',
              },
              { label: t(i18nKeys.PERCENTAGE), value: 'percentage' },
            ]}
            {...form.getInputProps('display')}
          />
        )}
        {renderDefaultValueField()}
        <Group justify="flex-end" mt="md">
          <Button type="submit" disabled={!form.isDirty()}>
            {t(i18nKeys.SAVE)}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
